<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import Icon from '@iconify/svelte';
	import { btn } from '$lib/actions';
	import Avatar from './Avatar.svelte';
	import Modal from './Modal.svelte';
	import { getOrCreateUserId } from '$lib/coop.svelte';
	import { getFriends } from '$lib/friends-global.svelte';
	import type { Friends, GameType } from '$lib/friends.svelte';
	import { showToast } from '$lib/toast';
	import { sound } from '$lib/sound';
	import BadgeFlair from './BadgeFlair.svelte';
	import { readGameState, type GameKey } from '$lib/progress';
	import { parseDayKey } from '$lib/daily';

	let friends = $state<Friends | null>(null);
	let confirmingReplace = $state(false);

	onMount(() => {
		friends = getFriends(getOrCreateUserId());
	});

	const GAME_PATH: Record<GameType, string> = {
		'word-guess': '/word-guess',
		'word-group': '/word-group',
		'word-build': '/word-build',
		'word-find': '/word-find',
		'word-cross': '/word-cross',
		'word-cross-m': '/word-cross-m',
		'word-crypto': '/word-crypto',
		'word-speed': '/word-speed',
		'num-fill': '/num-fill',
		'word-tower': '/word-tower'
	};
	const GAME_LABEL: Record<GameType, string> = {
		'word-guess': 'Word Guess',
		'word-group': 'Word Group',
		'word-build': 'Word Build',
		'word-find': 'Word Find',
		'word-cross': 'Word Cross S',
		'word-cross-m': 'Word Cross M',
		'word-crypto': 'Word Crypto',
		'word-speed': 'Word Speed',
		'num-fill': 'Num Fill',
		'word-tower': 'Word Tower'
	};
	const GAME_KEY: Record<GameType, GameKey> = {
		'word-guess': 'word-guess',
		'word-group': 'word-group',
		'word-build': 'word-build',
		'word-find': 'word-find',
		'word-cross': 'word-cross',
		'word-cross-m': 'word-cross-m',
		'word-crypto': 'word-crypto',
		'word-speed': 'word-speed',
		'num-fill': 'num-fill',
		'word-tower': 'word-tower'
	};

	let invite = $derived(friends?.state.incomingInvite ?? null);
	let outgoingInvite = $derived(friends?.state.outgoingInvite ?? null);

	// Reset confirmation state whenever the invite changes (new invite or cleared).
	let lastInviteRoom: string | null = null;
	$effect(() => {
		const r = invite?.roomId ?? null;
		if (r !== lastInviteRoom) {
			confirmingReplace = false;
			lastInviteRoom = r;
		}
	});

	const hasProgress = $derived.by(() => {
		if (!invite) return false;
		const date = parseDayKey(invite.dateKey) ?? new Date();
		const state = readGameState(GAME_KEY[invite.gameType], date);
		return !!state;
	});

	function attemptAccept() {
		if (!invite || !friends) return;
		if (hasProgress && !confirmingReplace) {
			confirmingReplace = true;
			return;
		}
		const path = GAME_PATH[invite.gameType];
		const url = `${path}?coop=${invite.roomId}&date=${invite.dateKey}`;
		friends.acceptInvite();
		// If we're already on this game's route (e.g. sitting in another lobby),
		// SvelteKit's client navigation won't reinitialize the coop instance with
		// the new roomId. Force a full reload so the new room's lobby boots up
		// cleanly.
		if (typeof window !== 'undefined' && window.location.pathname === path) {
			window.location.href = url;
		} else {
			goto(url);
		}
	}

	function decline() {
		friends?.declineInvite();
		showToast('Invite declined', 'neutral');
	}

	// Sound when a new invite arrives.
	let lastInviteId: string | null = null;
	$effect(() => {
		const id = invite?.roomId ?? null;
		if (id && id !== lastInviteId) sound.gameInvite();
		lastInviteId = id;
	});

	// Toast when partner declines (outgoing disappears unexpectedly).
	let lastOutgoingId: string | null = null;
	$effect(() => {
		const current = outgoingInvite?.roomId ?? null;
		if (lastOutgoingId && !current) {
			showToast('Invite declined', 'neutral');
		}
		lastOutgoingId = current;
	});
</script>

{#if invite}
	<Modal open={true} onclose={decline} title={confirmingReplace ? 'Replace your game?' : 'Friend wants to play'} maxWidth="max-w-xs" dismissable={false}>
		<div class="flex flex-col items-center gap-3">
			<Avatar avatar={invite.avatar} username={invite.username} size={10} extraClass="shrink-0" />

			{#if confirmingReplace}
				<p class="text-center text-base font-bold uppercase">
					You have a {GAME_LABEL[invite.gameType]} game in progress.
				</p>
				<p class="text-center text-sm font-bold uppercase opacity-70">
					Joining {invite.username}'s game will replace your current progress.
				</p>
			{:else}
				<div class="flex flex-col items-center gap-0">
					<p class="flex items-center justify-center gap-1 text-center text-base font-bold uppercase">
						{invite.username}
						<BadgeFlair badgeId={invite.featuredBadge ?? ''} />
					</p>
					<p class="text-center text-base font-bold uppercase opacity-70">wants to play</p>
				</div>
				<p class="text-center text-2xl font-bold uppercase">{GAME_LABEL[invite.gameType]} Co-op</p>
			{/if}

			<div class="flex w-full items-stretch gap-2 pt-2">
				<button use:btn type="button" onclick={decline} class="pixel-frame relative flex-1 cursor-pointer overflow-hidden">
					<div class="pixel-frame absolute inset-0 bg-(--absent-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--absent-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1 bg-(--absent-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--absent-frame-fill)"></div>
					<span class="relative flex h-full items-center justify-center px-3 py-3 text-base font-bold text-(--absent-frame-text) uppercase">
						{confirmingReplace ? 'Cancel' : 'Decline'}
					</span>
				</button>
				<button use:btn type="button" onclick={attemptAccept} class="pixel-frame relative flex-1 cursor-pointer overflow-hidden">
					<div class="pixel-frame absolute inset-0 bg-(--correct-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--correct-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1 bg-(--correct-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--correct-frame-fill)"></div>
					<span class="relative flex h-full items-center justify-center px-3 py-3 text-base font-bold text-(--correct-frame-text) uppercase">
						{confirmingReplace ? 'Replace' : 'Play'}
					</span>
				</button>
			</div>
		</div>
	</Modal>
{/if}
